import React, {useMemo, useState} from 'react';
import {EventOccurrenceDto} from '../../generated/models/EventOccurrenceDto';
import {formatDate} from '../../utils/dateUtils';

const MAX_VISIBLE_DATES = 3;

type RecurrenceInfoProps = {
  occurrences: EventOccurrenceDto[];
  expandable?: boolean;
  darkBackground?: boolean;
};

export const RecurrenceInfo: React.FC<RecurrenceInfoProps> = ({
  occurrences,
  expandable = false,
  darkBackground = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const sortedUpcoming = useMemo(() => {
    const now = new Date();
    return [...occurrences]
      .filter(o => new Date(o.dateTimeStart) >= now)
      .sort(
        (a, b) => new Date(a.dateTimeStart).getTime() - new Date(b.dateTimeStart).getTime(),
      );
  }, [occurrences]);

  const visibleDates =
    expandable && expanded
      ? sortedUpcoming
      : sortedUpcoming.slice(0, MAX_VISIBLE_DATES);
  const remainingCount = sortedUpcoming.length - visibleDates.length;

  const darkContainerOverride: React.CSSProperties = darkBackground
    ? {backgroundColor: 'rgba(99, 102, 241, 0.15)', borderColor: 'rgba(99, 102, 241, 0.4)'}
    : {};

  if (visibleDates.length === 0) {
    return (
      <div style={{...containerStyle, ...darkContainerOverride}}>
        <span style={badgeStyle}>🔁 Recurring Event</span>
        <p style={{...noUpcomingStyle, ...(darkBackground && {color: '#94a3b8'})}}>No upcoming occurrences</p>
      </div>
    );
  }

  return (
    <div style={{...containerStyle, ...darkContainerOverride}}>
      <span style={badgeStyle}>🔁 Recurring Event</span>
      <div style={dateListStyle}>
        <span style={{...labelStyle, ...(darkBackground && {color: '#cbd5e1'})}}>Upcoming dates:</span>
        {visibleDates.map((occurrence, index) => (
          <div key={index} style={{...dateItemStyle, ...(darkBackground && {color: '#ffffff'})}}>
            <span style={dotStyle}>•</span>
            <span>{formatDate(occurrence.dateTimeStart)}</span>
          </div>
        ))}
        {remainingCount > 0 && (
          <span
            style={{
              ...moreCountStyle,
              ...(expandable ? moreCountClickableStyle : {}),
            }}
            onClick={expandable ? () => setExpanded(true) : undefined}>
            +{remainingCount} more occurrence{remainingCount > 1 ? 's' : ''}
          </span>
        )}
        {expandable && expanded && (
          <span
            style={{...moreCountStyle, ...moreCountClickableStyle}}
            onClick={() => setExpanded(false)}>
            Show less
          </span>
        )}
      </div>
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '12px',
  padding: '12px',
  backgroundColor: 'rgba(99, 102, 241, 0.08)',
  borderRadius: '8px',
  border: '1px solid rgba(99, 102, 241, 0.2)',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  backgroundColor: '#6366f1',
  color: '#ffffff',
  padding: '4px 10px',
  borderRadius: '12px',
  fontSize: '0.8em',
  fontWeight: 600,
  width: 'fit-content',
};

const dateListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  paddingLeft: '4px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.85em',
  fontWeight: 600,
  color: '#475569',
  marginBottom: '2px',
};

const dateItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.85em',
  color: '#334155',
};

const dotStyle: React.CSSProperties = {
  color: '#6366f1',
  fontWeight: 'bold',
};

const noUpcomingStyle: React.CSSProperties = {
  fontSize: '0.85em',
  color: '#94a3b8',
  fontStyle: 'italic',
  margin: 0,
};

const moreCountStyle: React.CSSProperties = {
  fontSize: '0.8em',
  color: '#6366f1',
  fontWeight: 600,
  marginTop: '4px',
  paddingLeft: '14px',
};

const moreCountClickableStyle: React.CSSProperties = {
  cursor: 'pointer',
  textDecoration: 'underline',
};
